import classNames from "classnames";
import React, { useState } from "react";

import Dialog from "../../components/dialog/Dialog";
import LabeledPrice from "../../components/labeled-price/LabeledPrice.jsx";
import { MOBILE_BREAKPOINT } from "../../util/constants.js";
import { currencyFormat } from "../../util/i18n.js";
import "./styles.styl";

import { FormattedMessage, useIntl } from "react-intl";
import ToggleSwitch from "../../components/switch/ToggleSwitch";
import { CartNote } from "../cart-note/CartNote";


export default function CartSummary(props) {
  const intl = useIntl();

  const switchToEarlierDelivery = intl.formatMessage({
    id: "switchToEarlierDelivery",
  });

  const { delivery, fetchDelivery, canCancelCart } = props;
  const {
    editable,
    remaining_time,
    delivery_sale_price_in_cart_tax_customer_dependent,
    delivery_fee,
    is_delivery,
    deposit_sum,
    waive_delivery_above,
  } = delivery;
  let delivery_fee_display_val;
  if (
    waive_delivery_above &&
    delivery_sale_price_in_cart_tax_customer_dependent > waive_delivery_above
  ) {
    delivery_fee_display_val = 0;
  } else {
    delivery_fee_display_val = delivery_fee;
  }
  const fee_name = is_delivery
    ? intl.formatMessage({ id: "deliveryFee" })
    : intl.formatMessage({ id: "shippingFee" });

  const waive_delivery_fee =
    waive_delivery_above != null
      ? intl.formatMessage(
          { id: "waiveDeliveryFee" },
          { waive_delivery_above: waive_delivery_above },
        )
      : "";

  const nearestSpecificTimeslot = delivery?.nearest_specific_timeslot;
  const [showCancelConfirmationPopup, setShowCancelConfirmationPopup] = useState(false);
  const [showCancelSuccessPopup, setShowCancelSuccessPopup] = useState(false);
  let remaining_str = "";
  if (remaining_time) {
    remaining_str =
      "days" in remaining_time
        ? intl.formatMessage(
            { id: "remainingTimeDays" },
            { remaining_time: remaining_time["days"] },
          )
        : "hours" in remaining_time
          ? intl.formatMessage(
              { id: "remainingTimeHours" },
              { remaining_time: remaining_time["hours"] },
            )
          : "";
  }

  let timeLabel = intl.formatMessage(
    { id: "orderDeadline" },
    { remaining_str: remaining_str },
  );
  const isDesktop = window.innerWidth > MOBILE_BREAKPOINT;
  if (!editable) {
    timeLabel = intl.formatMessage({ id: "orderDeadlineExpired" });
  }

  const [showSwitchConfirmationPopup, setShowSwitchConfirmationPopup] =
    useState(false);
  const [switchConfirmationMessage, setSwitchConfirmationMessage] =
    useState("");
  const [isToggleLoading, setIsToggleLoading] = useState(false);

  const onSwitchToEarlierDateIfExists = () => {
    const currentDeliveryTimeslotDisplay = `${delivery.timeframe_short}, ${delivery.delivery_date}`;
    const newDeliveryTimeslotDisplay = `${nearestSpecificTimeslot.timeslot_display}`;
    const to = `${intl.formatMessage({ id: "to" })}`;
    const timeLeftMessage = intl.formatMessage(
      { id: "timeLeftMessage" },
      {
        remaining_time: nearestSpecificTimeslot.remaining_time,
        closing_time_str: nearestSpecificTimeslot.closing_time_str,
      },
    );
    const message = `
      <p>${intl.formatMessage({
        id: "changeYourDeliveryToThisEarlierDate",
      })}</p>
      <div class="timeslot-container" style="display: flex; justify-content: space-around; align-items: center;">
        <span class="current-timeslot">${currentDeliveryTimeslotDisplay}</span>
        <span style="margin: 0 10px; font-weight: bold;">${to}</span>
        <span class="new-timeslot">${newDeliveryTimeslotDisplay}</span>
      </div>
      <p>${timeLeftMessage}</p>
      ${
        delivery.orders.length > 0
          ? `<p>${intl.formatMessage({ id: "yourCartItemsWillBeLost" })}</p>
              <p>${intl.formatMessage({ id: "rulesWillBeReset" })}</p>`
          : ""
      }
    `;

    setSwitchConfirmationMessage(message);
    setShowSwitchConfirmationPopup(true);
  };

  const handleToggleForceEditable = async () => {
    setIsToggleLoading(true);
    try {
      await props.toggleForceEditable(delivery.id);
    } catch (error) {
      console.error("Failed to toggle force editable:", error);
    } finally {
      setIsToggleLoading(false);
    }
  };

  const handleCancelOrder = async () => {
    try {
      const response = await fetch(`/api/cart/${delivery.id}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to cancel order');
      }

      setShowCancelConfirmationPopup(false);
      setShowCancelSuccessPopup(true);

      fetchDelivery();

    } catch (error) {
      console.error('Error cancelling delivery:', error);
      alert(intl.formatMessage({
        id: "errorCancellingOrder",
        defaultMessage: "There was an error cancelling your order. Please try again."
      }));
    }
  };

  return (
    <div
      className={classNames("cart-summary", {
        "sticky-cart-summary": isDesktop,
      })}
    >
      <div className="cart-summary__boxprice">
        <div className="cart-summary__total-price">
          <LabeledPrice
            label={intl.formatMessage({ id: "totalPrice" })}
            price={currencyFormat(
              delivery_sale_price_in_cart_tax_customer_dependent,
            )}
          />
        </div>
      </div>
      <div className="cart-summary__deliveryfee">
        {deposit_sum > 0 && (
          <LabeledPrice
            label={intl.formatMessage({ id: "deposit" })}
            price={currencyFormat(deposit_sum)}
          />
        )}
        <div className="delivery-fee-container">
          <LabeledPrice
            label={fee_name}
            price={String(delivery_fee_display_val)}
          />
          {waive_delivery_above != null && (
            <div className="waive-delivery-text">{waive_delivery_fee}</div>
          )}
        </div>
      </div>
      <CartNote delivery={delivery} />
      <div className="cart-summary__extra">
        <div className="cart-summary__time">
          {props.isStaff && !remaining_str && !delivery.invoice_id ? (
            <div
              style={{ marginBottom: "10px" }}
              className="order-deadline-status"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                return false;
              }}
            >
              <ToggleSwitch
                name="is_force_editable"
                checked={delivery.is_force_editable}
                handleCheckBox={handleToggleForceEditable}
                isLoading={isToggleLoading}
              />
              <span className="force-editable-text">
                <FormattedMessage id="forceEditable" />
              </span>
            </div>
          ) : (
            <p className="ordering-deadline">{timeLabel}</p>
          )}
        </div>
        <div className="cart-summary__time">
          {delivery.sold &&
          !delivery.sold_and_edited &&
          delivery.orders.length > 0 && (
            <div className="order-status-container">
              <p className={`no-unsold-changes`}>
                <FormattedMessage id="checkedOrdered" />
              </p>
              {canCancelCart && editable && (
                <button
                  onClick={() => setShowCancelConfirmationPopup(true)}
                  className="cart-summary__cancel-button"
                >
                  ✘ <FormattedMessage id="cancelOrder" />
                </button>
              )}
            </div>
          )}
          {delivery.sold_and_edited && (
            <a
              href="#"
              onClick={(e) => {
                e.preventDefault();
                props.onPropagateOrderedAmount();
              }}
              className="unsold-changes"
            >
              <FormattedMessage id="resetChanges" />
            </a>
          )}
          {delivery.subscription.auto_buy &&
            delivery.sold &&
            !delivery.sold_and_edited &&
            delivery.orders.length === 0 && (
              <p className="cancelled-cart">
                <FormattedMessage id="cancelledOrPaused" />{" "}
              </p>
            )}
          {!delivery.subscription.auto_buy &&
            delivery.sold &&
            !delivery.sold_and_edited &&
            delivery.orders.length === 0 && (
              <p className="cancelled-cart">
                <FormattedMessage id="cancelled" />
              </p>
            )}
          {nearestSpecificTimeslot &&
            (props.nextEditableCart &&
            nearestSpecificTimeslot?.is_existing_timeslot_of_customer ? (
              <a
                href={props.nextEditableCart}
                style={{ color: props.colorSecondary }}
                className="nearest-specific-timeslot"
              >
                {switchToEarlierDelivery}
              </a>
            ) : (
              <a
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  onSwitchToEarlierDateIfExists();
                }}
                className="nearest-specific-timeslot"
                style={{ color: props.colorSecondary }}
              >
                {switchToEarlierDelivery}
              </a>
            ))}
          <Dialog
            colorSecondary={props.colorSecondary}
            logoSmallUrl={props.logoSmallUrl}
            onButtonClick={handleCancelOrder}
            onCloseDialogClick={() => setShowCancelConfirmationPopup(false)}
            active={showCancelConfirmationPopup}
            buttonText={<FormattedMessage id="cancelOrder" defaultMessage="Cancel order" />}
            cancelButtonText={<FormattedMessage id="keepOrder" defaultMessage="Keep order" />}
            showCancelButton={true}
          >
            <div className="cancel-order-confirmation">
              <p><FormattedMessage
                id="cancelOrderConfirmation"
                defaultMessage="Are you sure you want to cancel this order?"
              /></p>
              <p className="cancel-order-warning">
                <FormattedMessage
                  id="cancelOrderItemsWarning"
                  defaultMessage="All items in your cart will be removed."
                />
              </p>
            </div>
          </Dialog>
          <Dialog
            colorSecondary={props.colorSecondary}
            logoSmallUrl={props.logoSmallUrl}
            onButtonClick={() => {
              props.onSwitchToEarlierDateIfExists();
            }}
            onCloseDialogClick={() => {
              setShowSwitchConfirmationPopup(false);
            }}
            active={showSwitchConfirmationPopup}
            buttonText={intl.formatMessage({ id: "switchDelivery" })}
          >
            <div
              dangerouslySetInnerHTML={{ __html: switchConfirmationMessage }}
            />
          </Dialog>
          <Dialog
            colorSecondary={props.colorSecondary}
            logoSmallUrl={props.logoSmallUrl}
            onButtonClick={() => setShowCancelSuccessPopup(false)}
            onCloseDialogClick={() => setShowCancelSuccessPopup(false)}
            active={showCancelSuccessPopup}
            buttonText={<FormattedMessage id="ok" defaultMessage="OK" />}
            showCancelButton={false}
          >
            <p>
              <FormattedMessage
                id="cancelConfirmation"
                defaultMessage="Your order has been cancelled successfully."
              />
            </p>
          </Dialog>
        </div>
      </div>
    </div>
  );
}
